import {useQuery} from '@tanstack/react-query'
import {useNavigate, useParams} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useAuth} from '@/contexts/auth-context'
import {AccessProfileForm} from '@/components/AccessProfileForm'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {routes} from '@/lib/routes'
import type {AccessProfileCreateRequest, AccessProfileUpdateRequest} from '@/types/access-profile'
import {permissions} from '@/lib/permissions'
import {PageLayout} from '@/components/shared/PageLayout'
import {queryError} from '@/lib/query-utils'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {CONCEPT_ICON} from '@/lib/concepts'

export function AccessProfileEdit() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.accessProfiles.editPageTitle)
    const navigate = useNavigate()
    const {id} = useParams<{ id: string }>()
    const {hasPermission} = useAuth()

    const canUpdate = hasPermission(permissions.accessProfiles.update)

    // Fetch access profile
    const {data: profile, isLoading, error: fetchError} = useQuery({
        queryKey: ['access-profile', id],
        queryFn: () => services.accessProfiles.detail(id!),
        enabled: !!id,
    })

    const updateMutation = useEntityMutation({
        mutationFn: (data: AccessProfileUpdateRequest) =>
            services.accessProfiles.update(id!, data),
        invalidate: [['access-profiles'], ['access-profile', id]],
        successMessage: i18n.accessProfiles.profileUpdated,
        onSuccess: () => {
            navigate(routes.accessGovernance.profiles.detail(id!))
        },
    })

    const handleSubmit = async (data: AccessProfileCreateRequest | AccessProfileUpdateRequest) => {
        await updateMutation.mutateAsync(data as AccessProfileUpdateRequest)
    }

    if (!canUpdate) {
        navigate(routes.accessGovernance.profiles.detail(id!), {replace: true})
        return null
    }

    return (
        <PageLayout
            variant="detail"
            title={i18n.accessProfiles.editProfile}
            description={i18n.accessProfiles.editDescription}
            icon={CONCEPT_ICON.accessProfile}
            backTo={true}
            loading={isLoading}
            error={queryError(isLoading, profile, fetchError)}
            data-testid="access-profile-edit-page"
        >
            {profile && (
                <AccessProfileForm
                    profile={profile}
                    onSubmit={handleSubmit}
                    onCancel={() => navigate(-1)}
                    isLoading={updateMutation.isPending}
                />
            )}
        </PageLayout>
    )
}
